import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { PencilSimple, Trash, X } from "@phosphor-icons/react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const emptyForm = { kind: "weekly", weekday: "0", date: "", start: "09:00", end: "13:00", caregiver_name: "", notify: false };
const emptyAdd = { kind: "weekly", weekdays: [], date: "", blocks: [{ start: "08:30", end: "14:00" }], caregiver_name: "", notify: false };

const AddScheduleForm = ({ value, onChange, onSave, busy }) => {
  const toggleDay = (i) => {
    const cur = new Set(value.weekdays);
    if (cur.has(i)) cur.delete(i); else cur.add(i);
    onChange({ ...value, weekdays: Array.from(cur).sort() });
  };
  const setBlock = (i, k) => (e) => onChange({ ...value, blocks: value.blocks.map((b, idx) => (idx === i ? { ...b, [k]: e.target.value } : b)) });
  const entryCount = value.kind === "weekly" ? value.weekdays.length * value.blocks.filter((b) => b.start && b.end).length : 1;
  return (
    <div className="space-y-2" data-testid="schedule-add-form-inner">
      <div className="flex flex-wrap items-center gap-2">
        <select value={value.kind} onChange={(e) => onChange({ ...value, kind: e.target.value })} className="h-8 rounded-md border border-stone-300 px-2 text-xs" data-testid="schedule-add-kind">
          <option value="weekly">Recurring weekly</option>
          <option value="once">One-time date</option>
        </select>
        {value.kind === "once" && (
          <Input type="date" value={value.date} onChange={(e) => onChange({ ...value, date: e.target.value })} className="h-8 w-auto text-xs" data-testid="schedule-add-date" />
        )}
      </div>
      {value.kind === "weekly" && (
        <div className="flex flex-wrap gap-1.5" data-testid="schedule-add-days">
          {WEEKDAYS.map((d, i) => (
            <button key={d} type="button" onClick={() => toggleDay(i)} data-testid={`schedule-add-day-${i}`}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                value.weekdays.includes(i) ? "border-primary bg-primary text-primary-foreground" : "border-stone-300 bg-white text-stone-500 hover:border-primary/50 hover:text-primary"
              }`}>
              {d}
            </button>
          ))}
          {value.weekdays.length > 0 && (
            <button type="button" onClick={() => onChange({ ...value, weekdays: [] })} className="px-1 text-[10px] text-stone-400 hover:underline" data-testid="schedule-add-days-clear">clear</button>
          )}
        </div>
      )}
      <div className="space-y-1.5" data-testid="schedule-add-blocks">
        {value.blocks.map((b, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input type="time" value={b.start} onChange={setBlock(i, "start")} className="h-8 w-auto text-xs" data-testid={`schedule-add-start-${i}`} />
            <span className="text-xs text-stone-400">to</span>
            <Input type="time" value={b.end} onChange={setBlock(i, "end")} className="h-8 w-auto text-xs" data-testid={`schedule-add-end-${i}`} />
            {value.blocks.length > 1 && (
              <button type="button" onClick={() => onChange({ ...value, blocks: value.blocks.filter((_, idx) => idx !== i) })} className="text-stone-300 hover:text-[#C0392B]" data-testid={`schedule-add-block-del-${i}`}><X size={13} /></button>
            )}
          </div>
        ))}
        {value.blocks.length < 4 && (
          <button type="button" onClick={() => onChange({ ...value, blocks: [...value.blocks, { start: "16:00", end: "20:00" }] })}
            className="text-xs font-medium text-primary hover:underline" data-testid="schedule-add-block-more">
            + another shift time
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input value={value.caregiver_name} onChange={(e) => onChange({ ...value, caregiver_name: e.target.value })} placeholder="Caregiver (optional)" className="h-8 w-40 text-xs" data-testid="schedule-add-caregiver" />
        <label className="flex items-center gap-1.5 text-xs text-stone-600" data-testid="schedule-add-notify-label">
          <input type="checkbox" checked={value.notify} onChange={(e) => onChange({ ...value, notify: e.target.checked })} data-testid="schedule-add-notify" />
          Notify client &amp; caregiver
        </label>
        <Button size="sm" onClick={onSave} disabled={busy || (value.kind === "weekly" && entryCount === 0)} className="bg-primary text-primary-foreground" data-testid="schedule-add-save">
          {busy ? "Adding…" : value.kind === "weekly" ? `Add ${entryCount || ""} ${entryCount === 1 ? "entry" : "entries"}` : "Add"}
        </Button>
      </div>
    </div>
  );
};

const ScheduleForm = ({ value, onChange, onSave, onCancel, busy, saveLabel, tidPrefix }) => {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid={`${tidPrefix}-form`}>
      <select value={value.kind} onChange={set("kind")} className="h-8 rounded-md border border-stone-300 px-2 text-xs" data-testid={`${tidPrefix}-kind`}>
        <option value="weekly">Recurring weekly</option>
        <option value="once">One-time date</option>
      </select>
      {value.kind === "weekly" ? (
        <select value={value.weekday} onChange={set("weekday")} className="h-8 rounded-md border border-stone-300 px-2 text-xs" data-testid={`${tidPrefix}-weekday`}>
          {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
        </select>
      ) : (
        <Input type="date" value={value.date} onChange={set("date")} className="h-8 w-auto text-xs" data-testid={`${tidPrefix}-date`} />
      )}
      <Input type="time" value={value.start} onChange={set("start")} className="h-8 w-auto text-xs" data-testid={`${tidPrefix}-start`} />
      <Input type="time" value={value.end} onChange={set("end")} className="h-8 w-auto text-xs" data-testid={`${tidPrefix}-end`} />
      <Input value={value.caregiver_name} onChange={set("caregiver_name")} placeholder="Caregiver (optional)" className="h-8 w-40 text-xs" data-testid={`${tidPrefix}-caregiver`} />
      <label className="flex items-center gap-1.5 text-xs text-stone-600" data-testid={`${tidPrefix}-notify-label`}>
        <input type="checkbox" checked={value.notify} onChange={set("notify")} data-testid={`${tidPrefix}-notify`} />
        Notify client &amp; caregiver
      </label>
      <Button size="sm" onClick={onSave} disabled={busy} className="bg-primary text-primary-foreground" data-testid={`${tidPrefix}-save`}>{saveLabel}</Button>
      {onCancel && <Button size="sm" variant="ghost" onClick={onCancel} className="text-xs text-stone-400" data-testid={`${tidPrefix}-cancel`}><X size={13} /></Button>}
    </div>
  );
};

export const ScheduleCard = ({ clientId, isAdmin }) => {
  const [entries, setEntries] = useState([]);
  const [missed, setMissed] = useState(null);
  const [miles, setMiles] = useState(null);
  const [form, setForm] = useState(emptyAdd);
  const [editing, setEditing] = useState(null); // {id, ...form}
  const [anchor, setAnchor] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: mv }, { data: ms }] = await Promise.all([
        api.get(`/clients/${clientId}/schedule`),
        api.get(`/clients/${clientId}/missed-visits?days=30`),
        api.get(`/clients/${clientId}/care-milestones`),
      ]);
      setEntries(s.schedule || []);
      setMissed(mv);
      setMiles(ms);
      setAnchor(ms.checkin_anchor || "");
    } catch { /* non-fatal */ }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const payload = (f) => ({
    kind: f.kind, weekday: f.kind === "weekly" ? parseInt(f.weekday, 10) : undefined,
    date: f.kind === "once" ? f.date : undefined,
    start: f.start, end: f.end, caregiver_name: f.caregiver_name, notify: f.notify,
  });

  const add = async () => {
    setBusy(true);
    try {
      if (form.kind === "weekly") {
        const { data } = await api.post(`/clients/${clientId}/schedule/bulk`, {
          weekdays: form.weekdays,
          blocks: form.blocks.filter((b) => b.start && b.end),
          caregiver_name: form.caregiver_name, notify: form.notify,
        });
        toast.success(`${data.created} schedule ${data.created === 1 ? "entry" : "entries"} added${form.notify ? " — client & caregiver notified" : ""}`);
      } else {
        const b = form.blocks[0] || {};
        await api.post(`/clients/${clientId}/schedule`, { kind: "once", date: form.date, start: b.start, end: b.end, caregiver_name: form.caregiver_name, notify: form.notify });
        toast.success(form.notify ? "Schedule added — client & caregiver notified" : "Schedule entry added");
      }
      setForm(emptyAdd);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/clients/${clientId}/schedule/${editing.id}`, payload(editing));
      toast.success(editing.notify ? "Schedule updated — client & caregiver notified" : "Schedule entry updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (e) => {
    try {
      await api.delete(`/clients/${clientId}/schedule/${e.id}${e.notify ? "?notify=true" : ""}`);
      toast.success("Schedule entry removed");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveAnchor = async () => {
    try {
      await api.put(`/clients/${clientId}/checkin-anchor`, { checkin_anchor: anchor });
      toast.success("Check-in schedule updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mb-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="schedule-card">
      <p className="font-heading text-sm font-semibold text-stone-900">Visit schedule &amp; coverage</p>
      {miles && (
        <p className="mt-1 text-xs text-stone-500" data-testid="care-milestones">
          Next quarterly check-in: <strong className="text-stone-700">{miles.next_quarterly_checkin || "—"}</strong>
          {isAdmin && (
            <span className="ml-2 inline-flex items-center gap-1">
              (anchor
              <input type="date" value={anchor} onChange={(e) => setAnchor(e.target.value)}
                className="h-6 rounded border border-stone-300 px-1 text-[11px]" data-testid="checkin-anchor-input" />
              <button onClick={saveAnchor} className="rounded-full bg-stone-900 px-2 py-0.5 text-[10px] font-semibold text-white" data-testid="checkin-anchor-save">Set</button>
              )
            </span>
          )}
        </p>
      )}
      {missed && missed.scheduled > 0 && (
        <div className={`mt-2 rounded-md px-3 py-1.5 text-xs font-semibold ${missed.missed.length ? "bg-[#C0392B]/10 text-[#C0392B]" : "bg-[#27AE60]/10 text-[#1e8449]"}`} data-testid="missed-summary">
          Last {missed.window_days} days: {missed.covered}/{missed.scheduled} scheduled visits covered
          {missed.missed.length > 0 && (
            <ul className="mt-1 list-inside list-disc font-medium">
              {missed.missed.map((m) => (
                <li key={`${m.date}-${m.start}`} data-testid={`missed-slot-${m.date}`}>
                  {m.date} {m.start}–{m.end}{m.caregiver ? ` · ${m.caregiver}` : ""}
                  {m.reason ? ` — ${m.reason}` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {missed && missed.scheduled === 0 && (
        <p className="mt-2 text-xs text-stone-400">No schedule set yet — add the recurring or one-off schedule below so missed visits can be flagged.</p>
      )}
      <div className="mt-3 space-y-1.5" data-testid="schedule-list">
        {entries.map((e) => (
          <div key={e.id} className="rounded-lg border border-stone-100 px-3 py-1.5 text-xs" data-testid={`schedule-entry-${e.id}`}>
            {editing?.id === e.id ? (
              <ScheduleForm value={editing} onChange={setEditing} onSave={saveEdit} onCancel={() => setEditing(null)} busy={busy} saveLabel="Save" tidPrefix="schedule-edit" />
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-stone-700">
                  {e.kind === "weekly" ? `Every ${WEEKDAYS[e.weekday]}` : `Once on ${e.date}`} · {e.start}–{e.end}
                  {e.caregiver_name ? ` · ${e.caregiver_name}` : ""}
                </span>
                {isAdmin && (
                  <span className="flex items-center gap-2">
                    <button onClick={() => setEditing({ id: e.id, kind: e.kind, weekday: String(e.weekday ?? 0), date: e.date || "", start: e.start, end: e.end, caregiver_name: e.caregiver_name || "", notify: false })}
                      className="text-stone-300 hover:text-primary" data-testid={`schedule-edit-${e.id}`}><PencilSimple size={13} /></button>
                    <button onClick={() => remove(e)} className="text-stone-300 hover:text-[#C0392B]" data-testid={`schedule-del-${e.id}`}><Trash size={13} /></button>
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {isAdmin && (
        <div className="mt-3 border-t border-stone-100 pt-3" data-testid="schedule-add-form">
          <p className="mb-2 text-[11px] text-stone-500">Pick every day the visits happen, then each shift time — one save creates the whole week.</p>
          <AddScheduleForm value={form} onChange={setForm} onSave={add} busy={busy} />
        </div>
      )}
    </div>
  );
};
